import { IBlog, IBlogItem } from "@Interfaces/Api/blog";
import { Blog } from "Services/API/IndividualApi's/Blog";
import Link from "next/link";
import Image from "next/image";
import { GetStaticProps } from "next";
import { Box, Button, Card, CardContent, Container, Grid, Typography } from "@mui/material";
import { Layout } from "@Components/Layout";
import { StyledGridContainer } from "@Styled/Common";
import { StyledHeadingWithGradientunderline } from "@Styled/Pages/Landing/Category";
import { StyledCategoryFilterContainer } from "@Styled/Pages/Category";
import Head from "next/head";

interface Props {
  blogs: IBlogItem[],
  totalPages: number,
  currentPage: number
}


export default function BlogPage({ blogs, totalPages, currentPage }: Props) {

  return (
    <>
      <Head>
        <title>Blog | Econut</title>
        <meta name="description" content="Get latest updates from econut" />
      </Head>
      <Layout>
        <Container maxWidth="lg" sx={{ marginTop: { sm: "1rem", xs: "4rem" }, marginBottom: "5rem" }}>
          <StyledCategoryFilterContainer>
            <StyledHeadingWithGradientunderline
              variant="h1"
              sx={{
                fontSize: { sm: "1.5rem", xs: "1.2rem" },
                marginBottom: "1.5rem",
                letterSpacing: "-1px"
              }}
            >
              Our Blogs{" "}
            </StyledHeadingWithGradientunderline>
          </StyledCategoryFilterContainer>

          {blogs.length === 0 ? (
            <Typography align="center" color="text.secondary">
              No blogs available.
            </Typography>
          ) : (
            <>
              <Grid container spacing={4}>
                {blogs.map((blog) => (
                  <Grid item xs={12} sm={6} md={4} key={blog._id}>
                    <Link
                      href={`/blogs/${blog.slug}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          cursor: "pointer",
                          transition: "0.3s",
                          "&:hover": {
                            boxShadow: 6,
                          },
                        }}
                      >
                        {blog.coverImage?.url && (
                          <Box sx={{ position: "relative", height: 220 }}>
                            <Image
                              src={blog.coverImage.url}
                              alt={blog.title}
                              fill
                              style={{ objectFit: "cover" }}
                            />
                          </Box>
                        )}

                        <CardContent>
                          <Typography
                            variant="h6"
                            component="h2"
                            gutterBottom
                          >
                            {blog.title}
                          </Typography>

                          {blog.excerpt && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mb: 2,
                              }}
                            >
                              {blog.excerpt}
                            </Typography>
                          )}

                          {blog.publishedAt && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(
                                blog.publishedAt
                              ).toDateString()}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 6,
                  gap: 2,
                }}
              >
                {currentPage > 1 && (
                  <Button
                    variant="outlined"
                    component={Link}
                    href={`/blogs?page=${currentPage - 1}`}
                  >
                    Previous
                  </Button>
                )}

                <Typography>
                  Page {currentPage} of {totalPages}
                </Typography>

                {currentPage < totalPages && (
                  <Button
                    variant="outlined"
                    component={Link}
                    href={`/blogs?page=${currentPage + 1}`}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </>
          )}
        </Container>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const currentPage = Number(params?.page) || 1;
  const perPage = 9;

  // Fetch published blogs with pagination
  const apiResponse = await Blog.getPublished(
    `page=${currentPage}&perPage=${perPage}`
  );

  // 🔥 Safely unwrap result layer
  const response: IBlog = apiResponse?.result || apiResponse;
  const blogs = response?.data || [];
  const totalPages =
    response?.pagination?.totalCount && response?.pagination?.perPage
      ? Math.ceil(
        response.pagination.totalCount /
        response.pagination.perPage
      )
      : 1;

  return {
    props: {
      blogs,
      totalPages,
      currentPage
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 3, // In seconds
  };
}
